import React, { useEffect } from 'react';
import Button, { ButtonProps } from 'ui/elements/buttons/Button';
import useLazyResource from 'util/resource/useLazyResource';
import { usersApi } from 'apis/CompanyAPI/users/usersApi';
import { FeedbackContext } from 'types/user';
import i18n from 'util/i18n';
import useNotify from 'hooks/useNotify';
import CheckmarkIcon from 'ui/elements/icons/CheckmarkIcon';
import IconAvatar from 'ui/elements/icons/IconAvatar';
import { useCloseableMessage } from 'apis/CompanyAPI/users/useUiState';
import { sectionSpacing } from 'ui/theme/themeConstants';

export default function ContactMeButton({
  tag,
  feedback,
  companyId,
  buttonText,
  userWantsToBeContacted,
  buttonKind = 'primary',
}: {
  tag: string;
  feedback: FeedbackContext;
  companyId?: number;
  buttonText?: string;
  userWantsToBeContacted?: boolean;
  buttonKind?: ButtonProps['kind'];
}) {
  const { isClosed: hasSent, closeMessage: setHasSent, isLoading } = useCloseableMessage(tag);

  const notify = useNotify();

  const [sendFeedback, isSendingFeedback] = useLazyResource(
    (feedbackContext: FeedbackContext) =>
      usersApi.feedback({
        feedback: undefined,
        anonymous: false,
        feedbackContext: feedbackContext,
        companyId,
      }),
    {
      onSuccess: () => {
        setHasSent();
      },
      onFailure: () => {
        notify('error', i18n('en').errors.send('feedback'));
      },
    },
  );

  useEffect(() => {
    if (userWantsToBeContacted && !hasSent && !isLoading) {
      sendFeedback(feedback);
    }
  }, [userWantsToBeContacted, hasSent, isLoading]);

  return (
    !isLoading && (
      <div className="text-nowrap u-fade-in--quick">
        {hasSent ? (
          <div style={{ height: sectionSpacing }} className="u-flex u-flex--gap-quarter u-flex-align-center">
            <IconAvatar color="success" size={{ width: '20px', height: '20px' }}>
              <CheckmarkIcon />
            </IconAvatar>
            We will contact you soon.
          </div>
        ) : (
          <Button kind={buttonKind} color="blue" isLoading={isSendingFeedback} onClick={() => sendFeedback(feedback)}>
            {buttonText || 'Contact me'}
          </Button>
        )}
      </div>
    )
  );
}
